import type { AlertItem } from '../types';

/**
 * The one-click action an alert offers beyond "open the alert". Today only the system-run
 * channel restock alert has one: open the distribution plan builder for exactly the SKUs it
 * listed, with the alert's pinned plan settings, and compute on arrival.
 *
 * Shared by the alert detail card and the Home page "Actions to take" section so both offer
 * the same link.
 */
export interface AlertCta {
    /** Button text. */
    label: string;
    /** In-app route to navigate to. */
    href: string;
    /** Number of entities the action covers (SKUs). */
    count: number;
}

export const DISTRIBUTION_CHANNEL_UNDERSTOCK = 'DISTRIBUTION_CHANNEL_UNDERSTOCK';

export const REPLENISHMENT_QUESTION = 'REPLENISHMENT_QUESTION';

/** The inbound shipment watch: one rule each, the same rules the Needs chips on Shipments show. */
export const SHIPMENT_WATCH_ALERT_TYPES: ReadonlySet<string> = new Set([
    'SHIPMENT_DISPATCH_OVERDUE',
    'SHIPMENT_FORWARDER_MISSING',
    'SHIPMENT_ARRIVAL_OVERDUE',
]);
export const PAYMENT_DUE_SOON = 'PAYMENT_DUE_SOON';

/** Scheduled plans: a planning artifact re-run on a schedule, linked back by `alert.artifact`. */
export const REPLENISHMENT_OBJECTIVE_DUE = 'REPLENISHMENT_OBJECTIVE_DUE';
export const REBALANCE_PLAN_DUE = 'REBALANCE_PLAN_DUE';

/** Alert types whose open items are system-run actions, shown on the Home page rather than in Pending Actions. */
export const SYSTEM_ACTION_ALERT_TYPES: ReadonlySet<string> = new Set([
    DISTRIBUTION_CHANNEL_UNDERSTOCK,
    REPLENISHMENT_QUESTION,
    ...SHIPMENT_WATCH_ALERT_TYPES,
    PAYMENT_DUE_SOON,
    REPLENISHMENT_OBJECTIVE_DUE,
    REBALANCE_PLAN_DUE,
]);

/**
 * The generic case: the alert carries its own deep link (the type's default from its mapping, or
 * context.artifact written by the surface that created it; resolved per run → data.artifact). Route + fixed params + one param filled from the
 * breaching rows. Any alert type can use it; the per-type branches below are the older,
 * hand-wired ones.
 */
function ctaFromArtifact(alert: AlertItem): AlertCta | null {
    const a = alert.artifact;
    if (!a || typeof a.route !== 'string' || !a.route) return null;
    const rows = alert.breachingEntities ?? [];
    const count = rows.length;
    const search = new URLSearchParams(a.params ?? {});
    // The checkpoint's own id: the opened view can show where it came from and link back.
    if (alert.id) search.set('from', alert.id);
    if (a.rowParam?.name && a.rowParam.field) {
        const values = rows
            .map((r) => (r as Record<string, unknown>)[a.rowParam!.field])
            .filter((v): v is string => typeof v === 'string' && v.length > 0);
        if (values.length > 0) search.set(a.rowParam.name, [...new Set(values)].join(','));
    }
    const qs = search.toString();
    const base = a.label?.trim() || 'Open';
    return {
        label: count > 0 ? `${base} (${count})` : base,
        count,
        href: qs ? `${a.route}?${qs}` : a.route,
    };
}

export function buildAlertCta(alert: AlertItem): AlertCta | null {
    const generic = ctaFromArtifact(alert);
    if (generic) return generic;
    // The shipment watch and payments due: the list on the card is the list on the page.
    if (SHIPMENT_WATCH_ALERT_TYPES.has(alert.alertType as string)) {
        const count = (alert.breachingEntities ?? []).length;
        return {
            label: `Open shipments${count > 0 ? ` (${count})` : ''}`,
            count,
            href: '/shipments',
        };
    }
    if ((alert.alertType as string) === PAYMENT_DUE_SOON) {
        const count = (alert.breachingEntities ?? []).length;
        return {
            label: `Open payments${count > 0 ? ` (${count})` : ''}`,
            count,
            href: '/payments',
        };
    }
    // A saved planning question: the card opens the Ask tab on that question and re-runs it.
    if ((alert.alertType as string) === REPLENISHMENT_QUESTION) {
        const lines = (alert.breachingEntities ?? []).length;
        // The question travels as the intent the evaluation ran with (sourceParams.intent).
        const intent = alert.contextFilters?.intent;
        if (!intent || typeof intent !== 'object') return null;
        return {
            label: `Open the answer${lines > 0 ? ` (${lines} line${lines === 1 ? '' : 's'})` : ''}`,
            count: lines,
            href: `/replenishment?tab=ask&intent=${encodeURIComponent(JSON.stringify(intent))}`,
        };
    }
    if ((alert.alertType as string) !== DISTRIBUTION_CHANNEL_UNDERSTOCK) return null;
    const channelKey = alert.contextFilters?.channelKey;
    if (typeof channelKey !== 'string' || !channelKey) return null;
    const productIds = (alert.breachingEntities ?? [])
        .map((e) => (e as Record<string, unknown>).product_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
    if (productIds.length === 0) return null;
    // The alert's pinned plan settings (data.filters.a.sourceParams.planSettings) travel with
    // the link so the plan computed on arrival is the one the alert listed.
    const ps = alert.contextFilters?.planSettings;
    const psParam =
        ps && typeof ps === 'object' && !Array.isArray(ps) && Object.keys(ps).length > 0
            ? `&ps=${encodeURIComponent(JSON.stringify(ps))}`
            : '';
    return {
        label: `Open the distribution plan (${productIds.length} SKU${productIds.length === 1 ? '' : 's'})`,
        count: productIds.length,
        href:
            `/distribution?view=plan&channel=${encodeURIComponent(channelKey)}` +
            `&skus=${encodeURIComponent(productIds.join(','))}&autocompute=1${psParam}`,
    };
}

/** Sum of the engine's recommended units across the alert's rows (channel restock alert only). */
export function sumRecommendedUnits(alert: AlertItem): number {
    return (alert.breachingEntities ?? []).reduce((sum, e) => {
        const row = e as Record<string, unknown>;
        // Channel restock rows carry recommended_qty; a planning question's rows carry qty.
        const v = row.recommended_qty ?? row.qty;
        return sum + (typeof v === 'number' ? v : 0);
    }, 0);
}
